import React, { useState } from 'react';
import { Brain, TrendingUp, Target, Clock, CheckCircle, X, Loader } from 'lucide-react';
import { formatCurrency } from '../../../shared/utils';
import type { BudgetSuggestion, ReallocationSuggestion } from '../../../shared/types/budget';

interface BudgetSuggestionPanelProps {
  suggestions: BudgetSuggestion[];
  reallocations: ReallocationSuggestion[];
  loading: boolean;
  onAcceptSuggestion: (suggestion: BudgetSuggestion) => void;
  onRejectSuggestion: (suggestion: BudgetSuggestion) => void;
  onAcceptReallocation: (reallocation: ReallocationSuggestion) => void;
  onRejectReallocation: (reallocation: ReallocationSuggestion) => void;
  className?: string;
}

export const BudgetSuggestionPanel: React.FC<BudgetSuggestionPanelProps> = ({
  suggestions,
  reallocations,
  loading,
  onAcceptSuggestion,
  onRejectSuggestion,
  onAcceptReallocation,
  onRejectReallocation,
  className = ''
}) => {
  const [acceptedSuggestions, setAcceptedSuggestions] = useState<Set<string>>(new Set());
  const [rejectedSuggestions, setRejectedSuggestions] = useState<Set<string>>(new Set());

  const handleAcceptSuggestion = (suggestion: BudgetSuggestion) => {
    setAcceptedSuggestions(prev => new Set([...prev, suggestion.category_id]));
    onAcceptSuggestion(suggestion);
  };

  const handleRejectSuggestion = (suggestion: BudgetSuggestion) => {
    setRejectedSuggestions(prev => new Set([...prev, suggestion.category_id]));
    onRejectSuggestion(suggestion);
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 90) return 'text-green-600 dark:text-green-400';
    if (score >= 75) return 'text-blue-600 dark:text-blue-400';
    if (score >= 60) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'high': return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
      case 'medium': return 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20';
      default: return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
    }
  };

  if (loading) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center ${className}`}>
        <Loader className="h-8 w-8 text-blue-600 dark:text-blue-400 mx-auto mb-4 animate-spin" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          AI Budget Oracle Analyzing...
        </h3>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* AI Budget Suggestions */}
      {suggestions.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Brain className="h-6 w-6 text-purple-600 dark:text-purple-400 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  AI Budget Suggestions
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Intelligent recommendations based on market data and similar campaigns
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            {suggestions.map((suggestion) => {
              const isAccepted = acceptedSuggestions.has(suggestion.category_id);
              const isRejected = rejectedSuggestions.has(suggestion.category_id);
              
              return (
                <div
                  key={suggestion.category_id}
                  className={`border rounded-lg p-4 transition-all ${
                    isAccepted ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20' :
                    isRejected ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 opacity-60' :
                    'border-gray-200 dark:border-gray-700 hover:border-purple-200 dark:hover:border-purple-800'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white mr-3">
                          {formatCurrency(suggestion.suggested_amount)} Suggested
                        </h4>
                        <span className={`text-sm font-medium ${getConfidenceColor(suggestion.confidence_score)}`}>
                          {suggestion.confidence_score}% confidence
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        {suggestion.reasoning}
                      </p>
                      
                      {/* Market Data */}
                      {suggestion.market_data && (
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Genre Average:</span>
                            <span className="ml-1 font-medium text-gray-900 dark:text-white">
                              {formatCurrency(suggestion.market_data.genre_average)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Competition:</span>
                            <span className="ml-1 font-medium text-gray-900 dark:text-white capitalize">
                              {suggestion.market_data.competition_level}
                            </span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-gray-500 dark:text-gray-400">Recommended Timing:</span>
                            <span className="ml-1 font-medium text-gray-900 dark:text-white">
                              {suggestion.market_data.recommended_timing}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {!isAccepted && !isRejected && (
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleAcceptSuggestion(suggestion)}
                          className="inline-flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Accept
                        </button>
                        <button
                          onClick={() => handleRejectSuggestion(suggestion)}
                          className="inline-flex items-center px-3 py-1 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Reject
                        </button>
                      </div>
                    )}
                    
                    {isAccepted && (
                      <div className="flex items-center text-green-600 dark:text-green-400 ml-4">
                        <CheckCircle className="h-5 w-5 mr-1" />
                        <span className="text-sm font-medium">Accepted</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reallocation Suggestions */}
      {reallocations.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Reallocation Opportunities
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Optimize your current budget based on performance data
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            {reallocations.map((reallocation, index) => (
              <div
                key={index}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-blue-200 dark:hover:border-blue-800 transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white mr-3">
                        Move {formatCurrency(reallocation.amount)}
                      </h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getUrgencyColor(reallocation.urgency)}`}>
                        {reallocation.urgency} priority
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {reallocation.reason}
                    </p>
                    <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      {reallocation.potential_impact}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => onAcceptReallocation(reallocation)}
                      className="inline-flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <Target className="h-4 w-4 mr-1" />
                      Apply
                    </button>
                    <button
                      onClick={() => onRejectReallocation(reallocation)}
                      className="inline-flex items-center px-3 py-1 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {suggestions.length === 0 && reallocations.length === 0 && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <Brain className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Suggestions Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            The AI Budget Oracle will analyze your campaign and provide suggestions once more data is available.
          </p>
        </div>
      )}
    </div>
  );
};